import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge/badge';
import { cssMap, cx } from '@atlaskit/css';
import FilterIcon from '@atlaskit/icon/core/filter';
import { Popup } from '@atlaskit/popup/popup';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';

import { universalTypeFilterId } from '../../../../../../common/constants/filters';
import type { FilterId } from '../../../../../../common/constants/filters';
import ClearFilterButton from '../../../../../../common/ui/clear-filter-button';
import { RefreshedFilterButtonContext } from '../../../../../../common/ui/filters/filter-button/refreshed-filter-button-context';
import { isFilterSelectionCountable } from '../../../../../../common/utils/filters/is-filter-selection-countable';
import { usePrimaryProduct } from '../../../../../../controllers/store/bootstrap';
import {
	useFilters,
	useSearchActions,
	useSelectedProducts,
} from '../../../../../../controllers/store/search';
import { filter as archived } from '../../../../../../controllers/store/search/filters/archived';
import { filter as archivedAndPendingComponents } from '../../../../../../controllers/store/search/filters/archived-and-pending-components';
import { filter as assignee } from '../../../../../../controllers/store/search/filters/assignee';
import { filter as calendarType } from '../../../../../../controllers/store/search/filters/calendar-type';
import { filter as campaignStatus } from '../../../../../../controllers/store/search/filters/campaign-status';
import { filter as caseStatus } from '../../../../../../controllers/store/search/filters/case-status';
import { filter as closeDate } from '../../../../../../controllers/store/search/filters/close-date';
import { filter as commentedByMe } from '../../../../../../controllers/store/search/filters/commentedByMe';
import { filter as content } from '../../../../../../controllers/store/search/filters/content';
import { filter as contributor } from '../../../../../../controllers/store/search/filters/contributor';
import { filter as createdBy } from '../../../../../../controllers/store/search/filters/created-by';
import { filter as documentStatus } from '../../../../../../controllers/store/search/filters/document-status';
import { filter as focusAreaType } from '../../../../../../controllers/store/search/filters/focus-area-type';
import { filter as focusAreaUnder } from '../../../../../../controllers/store/search/filters/focus-area-under';
import { filter as folder } from '../../../../../../controllers/store/search/filters/folder';
import { filter as hasAttachment } from '../../../../../../controllers/store/search/filters/has-attachment';
import { filter as label } from '../../../../../../controllers/store/search/filters/label';
import { filter as lastUpdated } from '../../../../../../controllers/store/search/filters/last-updated';
import { filter as lastUpdatedBy } from '../../../../../../controllers/store/search/filters/last-updated-by';
import { filter as leadStatus } from '../../../../../../controllers/store/search/filters/lead-status';
import { filter as opportunityStatus } from '../../../../../../controllers/store/search/filters/opportunity-status';
import { filter as owner } from '../../../../../../controllers/store/search/filters/owner';
import { filter as pageStatus } from '../../../../../../controllers/store/search/filters/page-status';
import { filter as positionEmploymentType } from '../../../../../../controllers/store/search/filters/position-employment-type';
import { filter as positionFocusArea } from '../../../../../../controllers/store/search/filters/position-focus-area';
import { filter as positionJobFamily } from '../../../../../../controllers/store/search/filters/position-job-family';
import { filter as positionStatus } from '../../../../../../controllers/store/search/filters/position-status';
import { filter as positionTeam } from '../../../../../../controllers/store/search/filters/position-team';
import { filter as project } from '../../../../../../controllers/store/search/filters/project';
import { filter as pullRequestStatus } from '../../../../../../controllers/store/search/filters/pull-request-status';
import { filter as reporter } from '../../../../../../controllers/store/search/filters/reporter';
import { filter as repository } from '../../../../../../controllers/store/search/filters/repository';
import { filter as reviewer } from '../../../../../../controllers/store/search/filters/reviewer';
import { filter as salesforceStage } from '../../../../../../controllers/store/search/filters/salesforce-stage';
import { filter as site } from '../../../../../../controllers/store/search/filters/site';
import { filter as slackChannel } from '../../../../../../controllers/store/search/filters/slack-channel';
import { filter as space } from '../../../../../../controllers/store/search/filters/space';
import { filter as statusFilter } from '../../../../../../controllers/store/search/filters/status';
import { filter as tag } from '../../../../../../controllers/store/search/filters/tag';
import { filter as taskStatus } from '../../../../../../controllers/store/search/filters/task-status';
import { filter as taskType } from '../../../../../../controllers/store/search/filters/task-type';
import { filter as titleMatchOnly } from '../../../../../../controllers/store/search/filters/title-match-only';
import { filter as verified } from '../../../../../../controllers/store/search/filters/verified';
import { filter as watched } from '../../../../../../controllers/store/search/filters/watched';
import { filter as workspaceName } from '../../../../../../controllers/store/search/filters/workspace-name';
import ArchivedFilter from '../../../product-filters/archived';
import ArchivedAndPendingComponentsFilter from '../../../product-filters/archived-and-pending-components';
import AssigneeFilter from '../../../product-filters/assignee';
import CalendarTypeFilter from '../../../product-filters/calendar-type';
import CampaignStatusFilter from '../../../product-filters/campaign-status';
import CaseStatusFilter from '../../../product-filters/case-status';
import CloseDateFilter from '../../../product-filters/close-date';
import CommentedByMeFilter from '../../../product-filters/commented-by-me';
import ContentFilter from '../../../product-filters/content';
import ContributorFilter from '../../../product-filters/contributor';
import CreatedByFilter from '../../../product-filters/created-by';
import DocumentStatusFilter from '../../../product-filters/document-status';
import FocusAreaTypeFilter from '../../../product-filters/focus-area-type';
import FocusAreaUnderFilter from '../../../product-filters/focus-area-under';
import FolderFilter from '../../../product-filters/folder';
import HasAttachmentFilter from '../../../product-filters/has-attachment';
import LabelFilter from '../../../product-filters/label';
import LastUpdatedFilter from '../../../product-filters/last-updated';
import LastUpdatedByFilter from '../../../product-filters/last-updated-by';
import LeadStatusFilter from '../../../product-filters/lead-status';
import OpportunityStatusFilter from '../../../product-filters/opportunity-status';
import OwnerFilter from '../../../product-filters/owner';
import PageStatusFilter from '../../../product-filters/page-status';
import PositionEmploymentTypeFilter from '../../../product-filters/position-employment-type';
import PositionFocusAreaFilter from '../../../product-filters/position-focus-area';
import PositionJobFamilyFilter from '../../../product-filters/position-job-family';
import PositionStatusFilter from '../../../product-filters/position-status';
import PositionTeamFilter from '../../../product-filters/position-team';
import ProjectFilter from '../../../product-filters/project';
import PullRequestStatusFilter from '../../../product-filters/pull-request-status';
import ReporterFilter from '../../../product-filters/reporter';
import RepositoryFilter from '../../../product-filters/repository';
import ReviewerFilter from '../../../product-filters/reviewer';
import SalesforceStageFilter from '../../../product-filters/salesforce-stage';
import SiteSelectFilter from '../../../product-filters/site-select/site-select-filter';
import SlackChannelFilter from '../../../product-filters/slack-channel';
import SpaceFilter from '../../../product-filters/space';
import StatusFilter from '../../../product-filters/status';
import TagFilter from '../../../product-filters/tag';
import TaskStatusFilter from '../../../product-filters/task-status';
import TaskTypeFilter from '../../../product-filters/task-type';
import TitleMatchOnlyFilter from '../../../product-filters/title-match-only';
import UniversalTypeFilter from '../../../product-filters/universal-type';
import VerifiedFilter from '../../../product-filters/verified';
import WatchedFilter from '../../../product-filters/watched';
import WorkspaceNameFilter from '../../../product-filters/workspace-name';
import { useRefreshFilterPlacement } from '../../use-filter-placement';
import { i18n } from '../messages';

const styles = cssMap({
	trigger: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.050'),
		borderRadius: token('radius.small'),
		border: 'none',
		position: 'relative',
		backgroundColor: 'transparent',
		// 8px around the 16px icon makes the trigger 32px, matching the inline filters.
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		'&::after': {
			content: '""',
			position: 'absolute',
			inset: token('space.0'),
			borderRadius: 'inherit',
			borderStyle: 'solid',
			borderWidth: token('border.width'),
			borderColor: 'transparent',
			pointerEvents: 'none',
		},
	},
	triggerSelected: {
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
		'&::after': {
			borderColor: token('color.border.selected'),
		},
	},
	dropdownContainer: {
		width: '312px',
	},
	filtersContainer: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	clearButton: {
		paddingInlineStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
	},
	divider: {
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
});

// Placement is universal (QS-10764): site is always in the dropdown lookup. Visibility still
// follows the existing multi-site gate/experiment, unchanged from the old header selector.
// SiteSelectFilter itself checks the tenant's site count (it already reads unitMetadata for the
// current-site label), so this only needs the experiment check.
const RefreshRowSiteFilter = (): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();

	if (serveFpsMultiSiteSearchExperiment({ primaryProduct }) === 'control') {
		return null;
	}

	return <SiteSelectFilter isFilterInDropdown />;
};

const filters = {
	[universalTypeFilterId]: UniversalTypeFilter,
	[site.id]: RefreshRowSiteFilter,
	[lastUpdated.id]: LastUpdatedFilter,
	[createdBy.id]: CreatedByFilter,
	[contributor.id]: ContributorFilter,
	[lastUpdatedBy.id]: LastUpdatedByFilter,
	[owner.id]: OwnerFilter,
	[pageStatus.id]: PageStatusFilter,
	[space.id]: SpaceFilter,
	[tag.id]: TagFilter,
	[folder.id]: FolderFilter,
	[focusAreaType.id]: FocusAreaTypeFilter,
	[focusAreaUnder.id]: FocusAreaUnderFilter,
	[assignee.id]: AssigneeFilter,
	[reporter.id]: ReporterFilter,
	[project.id]: ProjectFilter,
	[content.id]: ContentFilter,
	[label.id]: LabelFilter,
	[statusFilter.id]: StatusFilter,
	[positionStatus.id]: PositionStatusFilter,
	[positionEmploymentType.id]: PositionEmploymentTypeFilter,
	[positionJobFamily.id]: PositionJobFamilyFilter,
	[positionTeam.id]: PositionTeamFilter,
	[positionFocusArea.id]: PositionFocusAreaFilter,
	[salesforceStage.id]: SalesforceStageFilter,
	[campaignStatus.id]: CampaignStatusFilter,
	[caseStatus.id]: CaseStatusFilter,
	[documentStatus.id]: DocumentStatusFilter,
	[calendarType.id]: CalendarTypeFilter,
	[leadStatus.id]: LeadStatusFilter,
	[taskStatus.id]: TaskStatusFilter,
	[taskType.id]: TaskTypeFilter,
	[closeDate.id]: CloseDateFilter,
	[opportunityStatus.id]: OpportunityStatusFilter,
	[pullRequestStatus.id]: PullRequestStatusFilter,
	[repository.id]: RepositoryFilter,
	[reviewer.id]: ReviewerFilter,
	[slackChannel.id]: SlackChannelFilter,
	[workspaceName.id]: WorkspaceNameFilter,
};

const checkboxFilters = {
	[watched.id]: WatchedFilter,
	[verified.id]: VerifiedFilter,
	[titleMatchOnly.id]: TitleMatchOnlyFilter,
	[archived.id]: ArchivedFilter,
	[archivedAndPendingComponents.id]: ArchivedAndPendingComponentsFilter,
	[commentedByMe.id]: CommentedByMeFilter,
	[hasAttachment.id]: HasAttachmentFilter,
};

const allFilterComponents = { ...filters, ...checkboxFilters };

export const FiltersDropdown = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { clearAllFilters } = useSearchActions();
	const allFilters = useFilters();
	const [selectedProducts] = useSelectedProducts();
	const { filtersInDropdown } = useRefreshFilterPlacement();

	const dropdownFilterIds = new Set<FilterId>(filtersInDropdown as FilterId[]);
	// For `site`, hasSelection already means "the user picked at least one additional site" —
	// the current/primary site is never written into the store's value (see the onSelectChange
	// guard in SiteSelectFilter), so the always-present default selection never counts here.
	const selectedFilterCount = Object.values(allFilters).filter(
		(filter) =>
			filter?.hasSelection &&
			dropdownFilterIds.has(filter.id) &&
			isFilterSelectionCountable(filter.id, selectedProducts),
	).length;

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleTriggerClick = useCallback(() => {
		setIsOpen((wasOpen) => !wasOpen);
	}, []);

	const isSelected = selectedFilterCount > 0 || isOpen;

	return (
		<Popup
			testId="search-page-refresh-filter-row-filters-dropdown"
			isOpen={isOpen}
			onClose={handleClose}
			placement="bottom-end"
			shouldRenderToParent
			role="dialog"
			label={formatMessage(i18n.dropdownLabel)}
			trigger={(triggerProps) => (
				<Pressable
					{...triggerProps}
					onClick={handleTriggerClick}
					aria-label={
						selectedFilterCount > 0
							? formatMessage(i18n.buttonLabelWithCount, { count: selectedFilterCount })
							: formatMessage(i18n.buttonLabel)
					}
					testId="search-page-refresh-filter-row-filters-control"
					xcss={cx(styles.trigger, isSelected && styles.triggerSelected)}
				>
					<FilterIcon
						color={isSelected ? token('color.icon.selected') : token('color.icon.subtle')}
						label=""
					/>
					{selectedFilterCount > 0 && (
						<Badge
							appearance="informationBold"
							testId="search-page-refresh-filter-row-filters-control-badge"
						>
							{selectedFilterCount}
						</Badge>
					)}
				</Pressable>
			)}
			content={() => (
				<RefreshedFilterButtonContext.Provider value>
					<Stack xcss={styles.dropdownContainer}>
						<Stack xcss={styles.filtersContainer} space="space.200">
							{filtersInDropdown.map((filterId) => {
								const Filter = allFilterComponents[filterId as keyof typeof allFilterComponents];
								if (!Filter) {
									return null;
								}
								return <Filter key={filterId} isFilterInDropdown />;
							})}
						</Stack>
						{selectedFilterCount > 0 && (
							<>
								<Box xcss={styles.divider} />
								<Box xcss={styles.clearButton}>
									<ClearFilterButton onClick={() => clearAllFilters()} />
								</Box>
							</>
						)}
					</Stack>
				</RefreshedFilterButtonContext.Provider>
			)}
		/>
	);
};
